import { useEffect, useState } from 'react';
import axios from 'axios';
import Badge from './Badge.jsx';
import PhoneLink from './PhoneLink.jsx';

// Compact activity panel that opens under a contact row when the user
// clicks the badges. Loads notes + emails scoped to that sub-contact
// and lets the user add a quick note inline. Dave 2026-06-13.
function SubContactActivityPanel({ subContactId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('Phone');
  const [savingNote, setSavingNote] = useState(false);

  function load() {
    setLoading(true);
    setError(null);
    axios.get(`/api/sub-contacts/${subContactId}/activity`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subContactId]);

  async function addNote() {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`/api/sub-contacts/${subContactId}/notes`, {
        text: noteText.trim(), type: noteType
      });
      setNoteText('');
      load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingNote(false);
    }
  }

  return (
    <div style={{
      padding: 'var(--space-3) var(--space-4)',
      background: 'var(--color-surface-sunken)',
      borderTop: '1px solid var(--color-border)'
    }}>
      {error && <p className="error">{error}</p>}

      {/* Quick-note composer */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
        <select
          value={noteType}
          onChange={(e) => setNoteType(e.target.value)}
          style={{ height: 'var(--input-h-sm)', fontSize: 'var(--text-small)' }}
        >
          <option value="Phone">Phone</option>
          <option value="Email">Email</option>
          <option value="Meeting">Meeting</option>
          <option value="Demo">Demo</option>
          <option value="Other">Other</option>
        </select>
        <input
          type="text"
          placeholder="Log a note about this contact…"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
          style={{ flex: 1 }}
        />
        <button type="button" className="btn-primary btn-sm" onClick={addNote} disabled={savingNote || !noteText.trim()}>
          {savingNote ? 'Saving…' : '+ Note'}
        </button>
      </div>

      {loading && <p>Loading activity…</p>}
      {!loading && data && data.notes.length === 0 && data.documents.length === 0 && (
        <p className="panel-empty" style={{ margin: 0 }}>No activity yet. Log a note above or send an email.</p>
      )}

      {/* Notes */}
      {!loading && data && data.notes.length > 0 && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--text-caption)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', color: 'var(--color-text-dim)', marginBottom: 4 }}>
            Notes ({data.notes.length})
          </div>
          {data.notes.map((n) => (
            <div key={n.id} style={{ padding: 'var(--space-2) 0', borderTop: '1px solid var(--color-border)' }}>
              <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                <span className="cell-mono">{n.note_date}</span>
                {' · '}{n.type}
                {n.user_name && <> · {n.user_name}</>}
              </div>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{n.text}</div>
            </div>
          ))}
        </div>
      )}

      {/* Emails sent */}
      {!loading && data && data.documents.length > 0 && (
        <div>
          <div style={{ fontSize: 'var(--text-caption)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)', color: 'var(--color-text-dim)', marginBottom: 4 }}>
            Emails Sent ({data.documents.length})
          </div>
          {data.documents.map((d) => (
            <div key={d.id} style={{ padding: 'var(--space-2) 0', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
              <div>
                <div style={{ fontWeight: 'var(--font-semibold)' }}>
                  {String(d.file_path || '').replace(/^email:/, '') || `Email #${d.id}`}
                </div>
                <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                  <span className="cell-mono">{d.sent_at ? String(d.sent_at).slice(0, 10) : '—'}</span>
                  {d.sender_name && <> · {d.sender_name}</>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {d.opened_at
                  ? <Badge tone="green">Opened {d.open_count > 1 ? `×${d.open_count}` : ''}</Badge>
                  : d.sent_at
                    ? <Badge tone="grey">Sent</Badge>
                    : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Compact contacts table with inline-edit rows. Drop-in section that
// renders below the contact card on both Customer and Lead detail
// pages. `contactId` is the parent contacts.id (customer or lead), the
// underlying API at /api/sub-contacts is agnostic to record_type.
//
// `items` is the array of sub_contact rows the parent loaded; the
// component sorts them itself (primary first, then last-name
// alphabetical). `onChanged` is called after each successful
// create / update / delete so the parent can reload from the server.

function ContactRow({ contactId, row, draft, onSaved, onCancelled, onDeleted, onSendEmail, expanded, onToggleExpand, onActivityChanged }) {
  const empty = {
    first_name: '', last_name: '', position: '',
    phone1: '', extension: '', email: '', is_primary: false
  };
  const initial = row
    ? {
        first_name: row.first_name || '',
        last_name:  row.last_name  || '',
        position:   row.position   || '',
        phone1:     row.phone1     || '',
        extension:  row.extension  || '',
        email:      row.email      || '',
        is_primary: !!row.is_primary
      }
    : empty;

  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(!!draft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function update(field) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (draft) {
        await axios.post('/api/sub-contacts', { contact_id: contactId, ...form });
      } else {
        await axios.put(`/api/sub-contacts/${row.id}`, form);
      }
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    if (draft) {
      if (onCancelled) onCancelled();
      return;
    }
    setForm(initial);
    setEditing(false);
    setError(null);
  }

  async function destroy() {
    if (!row) return;
    const fullName = [row.first_name, row.last_name].filter(Boolean).join(' ')
      || row.name
      || 'this contact';
    if (!window.confirm(`Delete ${fullName}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`/api/sub-contacts/${row.id}`);
      if (onDeleted) onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  }

  if (!editing) {
    const fullName = [row.first_name, row.last_name].filter(Boolean).join(' ').trim()
      || row.name
      || '(no name)';
    const phoneDisplay = row.phone1
      ? (row.extension ? `${row.phone1} ×${row.extension}` : row.phone1)
      : '';
    const emailCount = Number(row.email_count) || 0;
    const noteCount  = Number(row.note_count)  || 0;
    return (
      <>
      <tr className={`contact-row${row.is_primary ? ' contact-row-primary' : ''}${expanded ? ' contact-row-expanded' : ''}`}>
        <td>
          <div className="contact-name-cell" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span className="contact-name">{fullName}</span>
            {row.is_primary && <Badge tone="teal">Primary</Badge>}
            {/* Activity badges -- clickable to toggle the expansion. */}
            {(emailCount > 0 || noteCount > 0) && (
              <button
                type="button"
                onClick={() => onToggleExpand && onToggleExpand(row.id)}
                title={expanded ? 'Hide activity' : 'Show notes + emails'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-pill, 999px)',
                  padding: '2px 8px',
                  background: expanded ? 'var(--color-accent-soft)' : 'var(--color-surface)',
                  cursor: 'pointer', fontSize: 'var(--text-caption)',
                  color: 'var(--color-text-dim)'
                }}
              >
                {emailCount > 0 && <span>📧 {emailCount}</span>}
                {emailCount > 0 && noteCount > 0 && <span style={{ opacity: 0.5 }}>·</span>}
                {noteCount > 0 && <span>📝 {noteCount}</span>}
              </button>
            )}
            {/* "Add note" affordance even when no activity exists yet. */}
            {emailCount === 0 && noteCount === 0 && onToggleExpand && (
              <button
                type="button"
                onClick={() => onToggleExpand(row.id)}
                title="Log a note or view activity"
                style={{
                  border: '1px dashed var(--color-border-strong)',
                  borderRadius: 'var(--radius-pill, 999px)',
                  padding: '2px 8px',
                  background: 'transparent', cursor: 'pointer',
                  fontSize: 'var(--text-caption)',
                  color: 'var(--color-text-dim)'
                }}
              >
                + log
              </button>
            )}
          </div>
        </td>
        <td>{row.position || ''}</td>
        <td className="cell-mono">
          {row.phone1 ? <PhoneLink number={row.phone1} /> : ''}
          {row.phone1 && row.extension ? <span style={{ color: 'var(--color-text-dim)' }}> ×{row.extension}</span> : null}
        </td>
        <td className="cell-mono">{row.email ? <a href={`mailto:${row.email}`} style={{ color: 'inherit' }}>{row.email}</a> : ''}</td>
        <td>
          {(() => {
            if (!row.last_email_status) return <span className="cell-muted">—</span>;
            const tone = row.last_email_status === 'Opened'    ? 'green'
                       : row.last_email_status === 'Delivered' ? 'teal'
                       : row.last_email_status === 'Sent'      ? 'grey'
                       : row.last_email_status === 'Bounced'   ? 'red'
                       :                                         'grey';
            const sentDate = row.last_email_sent_at
              ? String(row.last_email_sent_at).slice(0, 10) : null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Badge tone={tone}>{row.last_email_status}</Badge>
                {sentDate && (
                  <span className="cell-mono" style={{ fontSize: 'var(--text-caption)', color: 'var(--color-text-dim)' }}>
                    {sentDate}
                  </span>
                )}
              </div>
            );
          })()}
        </td>
        <td className="contact-row-actions">
          {/* Send Email button -- only when the row has an email AND
              the parent passed an onSendEmail handler. Dave 2026-06-13:
              Zoe needs to email sub-contacts directly (Jane Smith at
              Guardian Fuel Tech, not just the distributor's main
              contact). */}
          {row.email && onSendEmail && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => onSendEmail(row)}
              title={`Send an email to ${row.email}`}
            >
              Send Email
            </button>
          )}
          <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm contact-row-delete"
            onClick={destroy}
            disabled={saving}
          >
            Delete
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="contact-row-activity">
          <td colSpan={6} style={{ padding: 0 }}>
            <SubContactActivityPanel
              subContactId={row.id}
              onChanged={onActivityChanged}
            />
          </td>
        </tr>
      )}
      </>
    );
  }

  return (
    <tr className={`contact-row contact-row-editing${draft ? ' contact-row-draft' : ''}`}>
      <td colSpan={6}>
        {error && <p className="error" style={{ marginBottom: 'var(--space-2)' }}>{error}</p>}
        <div className="contact-edit-grid">
          <label className="form-field">
            <span>First Name</span>
            <input
              value={form.first_name}
              onChange={update('first_name')}
              placeholder="Jane"
              autoFocus={draft}
            />
          </label>
          <label className="form-field">
            <span>Last Name</span>
            <input value={form.last_name} onChange={update('last_name')} placeholder="Smith" />
          </label>
          <label className="form-field contact-edit-position">
            <span>Position / Title</span>
            <input value={form.position} onChange={update('position')} placeholder="Store Manager" />
          </label>
          <label className="form-field contact-edit-phone">
            <span>Phone</span>
            <input
              value={form.phone1}
              onChange={update('phone1')}
              inputMode="tel"
              className="cell-mono"
              placeholder="555-555-1234"
            />
          </label>
          <label className="form-field contact-edit-ext">
            <span>Ext.</span>
            <input
              value={form.extension}
              onChange={update('extension')}
              inputMode="numeric"
              className="cell-mono"
              placeholder="101"
            />
          </label>
          <label className="form-field contact-edit-email">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={update('email')}
              className="cell-mono"
              placeholder="jane@example.com"
            />
          </label>
          <label className="form-field contact-edit-primary contact-primary-toggle">
            <input type="checkbox" checked={form.is_primary} onChange={update('is_primary')} />
            <span>Primary contact</span>
          </label>
        </div>
        <div className="contact-edit-actions">
          <button type="button" className="btn-secondary btn-sm" onClick={cancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (draft ? 'Add Contact' : 'Save')}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function ContactsSection({ contactId, items = [], onChanged, heading = 'Contacts', onSendEmail = null }) {
  const [drafting, setDrafting] = useState(false);
  // Which sub-contact row is currently expanded showing notes + emails.
  // null = none. Only one row can be expanded at a time so the table
  // doesn't blow up vertically. Dave 2026-06-13.
  const [expandedId, setExpandedId] = useState(null);

  const sorted = items.slice().sort((a, b) => {
    if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
    const an = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
    const bn = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
    return an.localeCompare(bn);
  });

  return (
    <section className="contacts-section">
      <div className="contacts-section-head">
        <h3>{heading}</h3>
        <div className="contacts-section-actions">
          <span className="result-count">
            {items.length} contact{items.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => setDrafting(true)}
            disabled={drafting}
            title={drafting ? 'Finish adding the current contact first' : 'Add a contact'}
          >
            + Add Contact
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table contacts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Position / Title</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Last Email</th>
              <th className="th-actions" />
            </tr>
          </thead>
          <tbody>
            {drafting && (
              <ContactRow
                contactId={contactId}
                row={null}
                draft={true}
                onSaved={() => { setDrafting(false); if (onChanged) onChanged(); }}
                onCancelled={() => setDrafting(false)}
              />
            )}

            {sorted.length === 0 && !drafting && (
              <tr>
                <td colSpan={6} className="empty-row">
                  No contacts yet — click <strong>+ Add Contact</strong> to add the first one.
                </td>
              </tr>
            )}

            {sorted.map((row) => (
              <ContactRow
                key={row.id}
                contactId={contactId}
                row={row}
                draft={false}
                onSaved={() => { if (onChanged) onChanged(); }}
                onDeleted={() => { if (onChanged) onChanged(); }}
                onSendEmail={onSendEmail}
                expanded={expandedId === row.id}
                onToggleExpand={(rid) => setExpandedId(expandedId === rid ? null : rid)}
                onActivityChanged={() => { if (onChanged) onChanged(); }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
